import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { TOUR_ANCHORS, TOUR_DISABLED_KEY } from "../lib/tourAnchors";

const PAD = 8;

function getRect(key) {
  const el = document.querySelector(`[data-tour="${key}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { el, top: r.top, left: r.left, width: r.width, height: r.height };
}

export default function GuidedTour({ route, open, onClose }) {
  const { t } = useTranslation();
  const anchors = useMemo(() => TOUR_ANCHORS[route] || [], [route]);
  const steps = useMemo(() => t(`tour.routes.${route}`, { returnObjects: true, defaultValue: [] }), [route, t]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);
  const rectRef = useRef(null);
  const scrolledIdxRef = useRef(-1);

  // Étapes valides : texte présent + (ancre null = bulle centrée, ou élément présent dans le DOM)
  const validIdx = useMemo(() => {
    return anchors.map((_, i) => i).filter((i) => steps[i] && (anchors[i] === null || getRect(anchors[i])));
  }, [anchors, steps, open]); // eslint-disable-line

  useEffect(() => { if (open) setIndex(0); }, [open, route]);

  // setRect UNIQUEMENT si la position bouge réellement (>=0.5px) → pas de re-render inutile ni de tremblement
  const measure = useCallback(() => {
    const realI = validIdx[index];
    if (realI == null || anchors[realI] === null) {
      if (rectRef.current !== null) { rectRef.current = null; setRect(null); }
      return;
    }
    const r = getRect(anchors[realI]);
    if (!r) { if (rectRef.current !== null) { rectRef.current = null; setRect(null); } return; }
    const p = rectRef.current;
    if (p && Math.abs(p.top - r.top) < 0.5 && Math.abs(p.left - r.left) < 0.5 && Math.abs(p.width - r.width) < 0.5 && Math.abs(p.height - r.height) < 0.5) return;
    rectRef.current = r;
    setRect(r);
  }, [validIdx, index, anchors]);

  // Défilement AU PLUS UNE FOIS par étape (garde par ref) → évite toute boucle scroll->render->scroll ("tremblement")
  useEffect(() => {
    if (!open) { scrolledIdxRef.current = -1; return; }
    if (scrolledIdxRef.current === index) return;
    const realI = validIdx[index];
    if (realI == null || anchors[realI] === null) { scrolledIdxRef.current = index; return; }
    const r = getRect(anchors[realI]);
    if (r) { r.el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" }); scrolledIdxRef.current = index; }
  }, [open, index, validIdx, anchors]);

  useEffect(() => {
    if (!open) return;
    measure();
    const onWin = () => { cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(measure); };
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    const iv = setInterval(measure, 400); // suit les contenus qui se chargent
    return () => { window.removeEventListener("resize", onWin); window.removeEventListener("scroll", onWin, true); clearInterval(iv); cancelAnimationFrame(rafRef.current); };
  }, [open, measure]);

  if (!open || validIdx.length === 0) return null;

  const total = validIdx.length;
  const step = steps[validIdx[index]] || {};
  const isLast = index >= total - 1;

  const finish = (disable) => {
    if (disable) localStorage.setItem(TOUR_DISABLED_KEY, "1");
    onClose?.();
  };
  const next = () => { if (isLast) finish(false); else setIndex((i) => Math.min(i + 1, total - 1)); };
  const prev = () => setIndex((i) => Math.max(i - 1, 0));

  // Position de la bulle + surbrillance, clampées à l'écran (sous la barre du haut)
  const vw = window.innerWidth, vh = window.innerHeight;
  const HEADER = 76;           // hauteur de la barre supérieure à ne pas masquer
  const cardW = Math.min(380, vw - 24);
  const cardH = 220;           // estimation de la hauteur de l'infobulle

  // Rectangle visible (intersection élément ∩ viewport), borné par le header
  let hole = null;
  if (rect) {
    const vTop = Math.max(rect.top - PAD, HEADER);
    const vBottom = Math.min(rect.top + rect.height + PAD, vh - 8);
    const vLeft = Math.max(rect.left - PAD, 8);
    const vRight = Math.min(rect.left + rect.width + PAD, vw - 8);
    hole = { top: vTop, left: vLeft, width: Math.max(0, vRight - vLeft), height: Math.max(0, vBottom - vTop) };
  }

  let cardTop, cardLeft, placement = "bottom";
  if (hole) {
    const below = vh - (hole.top + hole.height);
    const above = hole.top - HEADER;
    if (below >= cardH + 16) {
      cardTop = hole.top + hole.height + 14; placement = "bottom";
    } else if (above >= cardH + 16) {
      cardTop = hole.top - 14 - cardH; placement = "top";
    } else {
      // élément trop grand : infobulle centrée verticalement, lisible par-dessus
      cardTop = Math.max(HEADER + 8, (vh - cardH) / 2); placement = "bottom";
    }
    const centerX = hole.left + hole.width / 2;
    cardLeft = Math.min(Math.max(12, centerX - cardW / 2), vw - cardW - 12);
  } else {
    cardTop = vh / 2 - cardH / 2; cardLeft = vw / 2 - cardW / 2;
  }
  cardTop = Math.min(Math.max(cardTop, HEADER + 8), vh - cardH - 12);

  return createPortal(
    <div className="fixed inset-0 z-[200]" data-testid="guided-tour" style={{ pointerEvents: "auto" }}>
      {/* Overlay sombre avec trou (spotlight) via 4 panneaux */}
      {hole ? (
        <>
          <div className="absolute bg-black/65" style={{ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) }} onClick={() => {}} />
          <div className="absolute bg-black/65" style={{ top: hole.top + hole.height, left: 0, right: 0, bottom: 0 }} />
          <div className="absolute bg-black/65" style={{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height }} />
          <div className="absolute bg-black/65" style={{ top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height }} />
          <div className="absolute rounded-xl pointer-events-none" style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height, boxShadow: "0 0 0 2px rgba(56,189,248,0.9), 0 0 0 6px rgba(56,189,248,0.25)" }} />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/65" />
      )}

      {/* Bulle */}
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, y: placement === "bottom" ? -8 : 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="absolute glass card-3d rounded-2xl p-5 border border-sky-400/30 shadow-2xl"
          style={{ top: cardTop, left: cardLeft, width: cardW }}
          data-testid="tour-tooltip"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-sky-300">{t("tour.stepOf", { i: index + 1, n: total })}</span>
            <button onClick={() => finish(false)} data-testid="tour-skip" className="text-xs text-muted-foreground hover:text-white">{t("tour.skip")}</button>
          </div>
          <h3 className="font-display font-extrabold text-lg leading-tight">{step.title}</h3>
          <p className="text-sm text-foreground/80 mt-1.5 leading-relaxed">{step.body}</p>
          <div className="flex items-center justify-between mt-4 gap-2">
            <button onClick={() => finish(true)} data-testid="tour-dont-show" className="text-[11px] text-muted-foreground hover:text-white underline-offset-2 hover:underline">{t("tour.dontShow")}</button>
            <div className="flex items-center gap-2">
              {index > 0 && (
                <button onClick={prev} data-testid="tour-prev" className="text-sm font-medium rounded-xl px-3 h-9 border border-white/15 hover:bg-white/5 transition">{t("tour.prev")}</button>
              )}
              <button onClick={next} data-testid="tour-next" className="text-sm font-semibold rounded-xl px-4 h-9 text-white transition" style={{ background: "linear-gradient(135deg,#0284c7,#0ea5e9)" }}>
                {isLast ? t("tour.finish") : t("tour.next")}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body
  );
}
